/**
 * Products Tab - Product list and management
 */

import React, { useState, useEffect } from 'react';
import { Search, Loader2, Tag, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../i18n';
import { apiGetAllProducts } from '../../../services/api';
import SKUManagementModal from '../SKUManagementModal';
import { logger } from '../../../utils/logger';

interface Product {
  id: string;
  name: string;
  category: string;
  base_cost?: number;
  size?: number;
  weight?: number;
  width?: number;
  height?: number;
  length?: number;
  productSku?: string | null;
}

interface ProductsTabProps {
  onStatsChange?: () => void;
}

const ProductsTab: React.FC<ProductsTabProps> = ({ onStatsChange }) => {
  const { t } = useLanguage();

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const data = await apiGetAllProducts();
      const mapped = data.map((p) => ({
        ...p,
        baseCost: p.base_cost ?? p.baseCost,
        productSku: p.product_sku ?? p.productSku,
      })) as unknown as Product[];
      setProducts(mapped);
    } catch (error) {
      logger.error('Failed to load products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleOpenSKUModal = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleCloseSKUModal = () => {
    setSelectedProduct(null);
  };

  const handleSKUUpdate = () => {
    onStatsChange?.();
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [productSearch]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              Product Management Moved to StockPulse
            </h3>
            <p className="text-sm text-blue-700">
              Product data is now managed centrally in StockPulse. Use the Catalog API Sync or Excel Upload features in StockPulse to add or update products. This page displays products in read-only mode.
            </p>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder={t.settings.searchProducts}
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Products count */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{filteredProducts.length} / {products.length} {t.settings.product}</span>
        {totalPages > 1 && (
          <span>
            Sayfa {currentPage} / {totalPages} (Gösterilen: {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)})
          </span>
        )}
      </div>

      {/* Products table */}
      {isLoadingProducts ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          {products.length === 0 ? t.settings.noProducts : t.settings.noSearchResults}
        </div>
      ) : (
        <div className="overflow-x-auto bg-slate-50 rounded-lg">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">
                  {t.settings.productName}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">
                  {t.settings.category}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">
                  {t.settings.cost}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">
                  Desi
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">
                  {t.settings.weight}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">
                  {t.settings.dimensions}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">
                  SKU
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">
                  {t.settings.actions}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-slate-100">
                  <td className="px-4 py-3 text-sm text-slate-800">
                    {product.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {product.category || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-right">
                    {product.base_cost ? `$${Number(product.base_cost).toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">
                    {product.size ? Number(product.size).toFixed(1) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">
                    {product.weight ? Number(product.weight).toFixed(2) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center">
                    {product.width && product.height && product.length
                      ? `${Number(product.width).toFixed(0)}×${Number(product.height).toFixed(0)}×${Number(product.length).toFixed(0)}`
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 text-center font-mono text-xs">
                    {product.productSku || '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleOpenSKUModal(product)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                    >
                      <Tag className="w-3.5 h-3.5" />
                      SKU
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-slate-100 border-t border-slate-200">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Önceki
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 text-sm font-medium rounded ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki
              </button>
            </div>
          )}
        </div>
      )}

      {/* SKU Management Modal */}
      {selectedProduct && (
        <SKUManagementModal
          product={selectedProduct}
          isOpen={true}
          onClose={handleCloseSKUModal}
          onUpdate={handleSKUUpdate}
        />
      )}
    </div>
  );
};

export default ProductsTab;
